<!-- eslint-disable vue/max-lines-per-block -->
<script setup lang="ts">
import {
  CheckBadgeIcon,
  DocumentCurrencyEuroIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'
import { useField, useFieldArray, useFormContext } from 'vee-validate'
import { onMounted, ref, watch } from 'vue'

import type { ContactEmailDynamic } from '@/types/external/2work/doctypes/generatorOutput/Contacts/ContactEmailDynamic'
import type { TWOrganizationAIResponse } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWOrganizationAIResponse'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import CollapsableContent from '@/components/common/layout/CollapsableContent.vue'
import ButtonTertiary from '@/components/common/ui/buttons/ButtonTertiary.vue'
import InputGrid from '@/components/common/ui/inputs/InputGrid.vue'
import TextInput from '@/components/common/ui/inputs/TextInput.vue'
import ThreedotActionsOnHover from '@/components/common/ui/inputs/ThreedotActionsOnHover.vue'
import ContextMenuItem from '@/components/common/ui/overlays/ContextMenuItem.vue'
import { hasMatchingAiEmail } from '@/utilities/organizationAi'
import { translationFunctionDefault } from '@/utilities/translations'

interface SectionEmailsProps {
  addEmailDisabled?: boolean | undefined
  aiData?: null | TWOrganizationAIResponse | undefined
  // Only customers have an invoice email, which accounting emails are sent to
  allowInvoiceEmail?: boolean | undefined
  checkPrimary?: ((email: ContactEmailDynamic) => boolean) | undefined
  inCreateContext?: boolean
  onSubmit?: (() => void) | undefined
}

const props = defineProps<SectionEmailsProps>()

const aiFilledFields = ref<Set<string>>(new Set())

const getAiFieldCount = (): number => {
  return aiFilledFields.value.size
}

const isAiFieldsStillVisible = (): boolean => {
  return getAiFieldCount() > 0
}

const acceptAIFields = (): void => {
  aiFilledFields.value.clear()
}

const tfDefault = translationFunctionDefault()

// TODO: find right generic type
const { setFieldValue } = useFormContext()
const emailFieldArray = useFieldArray<ContactEmailDynamic>('email_ids')

const handleAddEmail = (): void => {
  emailFieldArray.push({
    doctype: 'Contact Email',
    email_id: '',
    is_primary: emailFieldArray.fields.value.length === 0 ? 1 : 0,
  })
}

const isPrimaryEmail = (email: ContactEmailDynamic): boolean => {
  if (props.checkPrimary) {
    return props.checkPrimary(email)
  }
  // TODO: boolean or number value here? (make backend consistent)
  const emailPrimaryValue = 1

  return email.is_primary === emailPrimaryValue
}

const isInvoiceEmail = (email: ContactEmailDynamic): boolean => {
  return !!props.allowInvoiceEmail && email.tw_is_invoice_email === 1
}

const getEmailLabel = (email: ContactEmailDynamic): string => {
  const isPrimary = isPrimaryEmail(email)

  if (isInvoiceEmail(email)) {
    return isPrimary
      ? tfDefault('editRelationGroup.emailsTitlePrimaryInvoice')
      : tfDefault('editRelationGroup.emailsTitleInvoice')
  }

  return isPrimary
    ? tfDefault('editRelationGroup.emailsTitlePrimary')
    : tfDefault('editRelationGroup.emailsTitle')
}

// Only one email can be the invoice email, setting it removes the flag from all others
const handleToggleInvoiceEmail = (emailItemIndex: number): void => {
  const setAsInvoiceEmail =
    emailFieldArray.fields.value[emailItemIndex]?.value.tw_is_invoice_email !== 1

  for (const [currentEmailFieldIndex] of emailFieldArray.fields.value.entries()) {
    setFieldValue(
      `email_ids.${currentEmailFieldIndex}.tw_is_invoice_email`,
      setAsInvoiceEmail && currentEmailFieldIndex === emailItemIndex ? 1 : 0,
    )
  }

  props.onSubmit?.()
}

const handleSetAsPrimary = (emailID?: string): void => {
  if (emailID) {
    emailFieldArray.fields.value.forEach((currentEmailField, currentEmailFieldIndex) => {
      if (currentEmailField.value.email_id === emailID) {
        setFieldValue(`email_ids.${currentEmailFieldIndex}.is_primary`, 1)
      } else {
        setFieldValue(`email_ids.${currentEmailFieldIndex}.is_primary`, 0)
      }
    })

    props.onSubmit?.()
  }
}

const handleRemoveEmail = (emailItemIndex: number, submit = true): void => {
  const currentItem = emailFieldArray.fields.value[emailItemIndex]

  if (currentItem) {
    const itemPrimaryValue = 1
    const currentItemPrimaryValue = currentItem.value.is_primary
    const isCurrentItemPrimary = currentItemPrimaryValue === itemPrimaryValue

    const aiPathsToRemove = ['email_id']

    aiPathsToRemove.forEach((fieldName) => {
      const fullPath = `email_ids.${emailItemIndex}.${fieldName}`
      aiFilledFields.value.delete(fullPath)
    })

    for (
      let emailIndex = emailItemIndex + 1;
      emailIndex < emailFieldArray.fields.value.length;
      emailIndex++
    ) {
      aiPathsToRemove.forEach((fieldName) => {
        const oldPath = `email_ids.${emailIndex}.${fieldName}`
        const newPath = `email_ids.${emailIndex - 1}.${fieldName}`

        if (aiFilledFields.value.has(oldPath)) {
          aiFilledFields.value.delete(oldPath)
          aiFilledFields.value.add(newPath)
        }
      })
    }

    emailFieldArray.remove(emailItemIndex)

    if (isCurrentItemPrimary) {
      const firstItemPositionIndex = 0
      const firstItem = emailFieldArray.fields.value[firstItemPositionIndex]

      if (firstItem) {
        const firstItemPrimaryValue = firstItem.value.is_primary
        if (firstItemPrimaryValue !== itemPrimaryValue) {
          setFieldValue(
            `email_ids.${firstItemPositionIndex.toFixed()}.is_primary`,
            itemPrimaryValue,
          )
        }
      }
    }

    if (submit) props.onSubmit?.()
  }
}

const clearAIFields = (): void => {
  for (let emailIndex = emailFieldArray.fields.value.length - 1; emailIndex >= 0; emailIndex--) {
    const basePath = `email_ids.${emailIndex}`
    const hasAiFields = ['email_id'].some((fieldName) =>
      aiFilledFields.value.has(`${basePath}.${fieldName}`),
    )

    if (hasAiFields) {
      handleRemoveEmail(emailIndex, false)
    }
  }
}

const emailExistsAndFilled = (index: number): boolean => {
  const email = emailFieldArray.fields.value[index]?.value
  if (!email) return false

  return !!email.email_id
}

const fillFields = (): void => {
  const aiEmail = props.aiData?.email
  if (!aiEmail || aiEmail.length === 0) return

  aiEmail.forEach((email_id) => {
    const existingEmails = emailFieldArray.fields.value.map((email) => email.value.email_id)
    if (hasMatchingAiEmail(existingEmails, email_id)) return

    const targetIndex = emailFieldArray.fields.value.length
    const is_primary = targetIndex === 0 ? (1 as const) : (0 as const)

    emailFieldArray.push({
      doctype: 'Contact Email',
      email_id: '',
      is_primary,
    })

    if (!emailExistsAndFilled(targetIndex)) {
      const fields = [{ path: `email_ids.${targetIndex}.email_id`, value: email_id }]

      fields.forEach(({ path, value }) => {
        const { value: fieldValue } = useField(path)
        if (!fieldValue.value && value) {
          setFieldValue(path, value)
          aiFilledFields.value.add(path)
        }
      })
    }
  })
}

onMounted(() => {
  fillFields()
})

watch(
  () => props.aiData,
  (newVal) => {
    if (newVal) {
      clearAIFields()
      fillFields()
    }
  },
)

defineExpose({
  acceptAIFields,
  clearAIFields,
  getAiFieldCount,
  isAiFieldsStillVisible,
})
</script>

<template>
  <CollapsableContent :title="tfDefault('editRelationGroup.emailsSectionTitle')">
    <ThreedotActionsOnHover
      v-for="(currentEmail, currentEmailIndex) in emailFieldArray.fields.value"
      :key="currentEmail.key"
      menu-align="right"
      position="center right"
      visibility="hover"
    >
      <InputGrid :data-testid="TEST_IDS_CREATE.sectionEmails.inputGrid">
        <TextInput
          :capitalize-first="false"
          :data-testid="TEST_IDS_CREATE.sectionEmails.emailInput"
          :in-create-context="inCreateContext ?? false"
          :is-filled-by-ai="aiFilledFields.has(`email_ids.${currentEmailIndex}.email_id`)"
          :label="getEmailLabel(currentEmail.value)"
          :name="`email_ids.${currentEmailIndex}.email_id`"
          on-white-bg
          @blur="onSubmit"
          @manual-input="() => aiFilledFields.delete(`email_ids.${currentEmailIndex}.email_id`)"
        />
      </InputGrid>
      <template #actions>
        <ContextMenuItem
          v-if="!isPrimaryEmail(currentEmail.value)"
          key="set-as-primary"
          :icon="CheckBadgeIcon"
          :text="tfDefault('editRelationGroup.emailSetAsPrimaryLabel')"
          @click="() => handleSetAsPrimary(currentEmail.value.email_id)"
        />
        <ContextMenuItem
          v-if="allowInvoiceEmail"
          key="toggle-invoice-email"
          :icon="DocumentCurrencyEuroIcon"
          :text="
            isInvoiceEmail(currentEmail.value)
              ? tfDefault('editRelationGroup.emailUnsetAsInvoiceLabel')
              : tfDefault('editRelationGroup.emailSetAsInvoiceLabel')
          "
          @click="() => handleToggleInvoiceEmail(currentEmailIndex)"
        />
        <ContextMenuItem
          :icon="TrashIcon"
          :text="tfDefault('editRelationGroup.remove')"
          @click="() => handleRemoveEmail(currentEmailIndex)"
        />
      </template>
    </ThreedotActionsOnHover>
    <ButtonTertiary
      :data-testid="TEST_IDS_CREATE.sectionEmails.addButton"
      :disabled="addEmailDisabled"
      :label="tfDefault('editRelationGroup.emailAddButtonLabel')"
      @click="handleAddEmail"
    >
      <PlusCircleIcon class="mr-2 h-6 w-6" />
    </ButtonTertiary>
  </CollapsableContent>
</template>
